import RefreshIcon from '@mui/icons-material/Refresh';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { fileKindService } from 'services/fileService';
import { useCooldown } from 'hooks/useCooldown';
import { useGalleryContext } from '..';
import { identitySelector } from '../selectors';
import { ActionButton } from './actionButton';
import { showToast } from 'services/toaster';
import { TOASTER_TYPES } from 'components/core/Toaster/types';

interface RefreshPreviewButtonProps {
  isMobile: boolean;
}

const REFRESH_PREVIEW_COOLDOWN_MS = 3000;

export const RefreshPreviewButton: React.FC<RefreshPreviewButtonProps> = ({
  isMobile,
}) => {
  const intl = useIntl();
  const [file] = useGalleryContext().useMainDisplayedFile(identitySelector);
  const refreshPreview = useGalleryContext().refreshPreview;
  const { isCoolingDown, triggerCooldown } = useCooldown(
    REFRESH_PREVIEW_COOLDOWN_MS
  );

  const isPendingPdfConversion =
    !!file && fileKindService.isPendingConversionPdf(file);

  const label = formatTranslation(intl, CommonTranslationKeys.refresh);

  const handleRefresh = useCallback(async (): Promise<void> => {
    if (isCoolingDown) return;

    triggerCooldown();
    
    try {
      const isConversionComplete = await refreshPreview?.();
      if (!isConversionComplete) {
        showToast({
            message: { id: 'image_convert_info_pending' },
            type: TOASTER_TYPES.SUCCESS,
          });
      }
    } catch {
      // Ignore errors - refresh is best-effort
    }
  }, [isCoolingDown, refreshPreview, triggerCooldown]);

  if (!isPendingPdfConversion || !refreshPreview) {
    return null;
  }

  return (
    <ActionButton
      isMobile={isMobile}
      onClick={handleRefresh}
      disabled={isCoolingDown}
      tooltipTitle={label}
      label={label}
      icon={<RefreshIcon />}
    />
  );
};
